import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import Checkbox from 'components/Form/Checkbox';
import Field from 'components/Form/Field';
import FormResult from 'components/Form/FormResult';
import InputEmail from 'components/Form/InputEmail';
import SubmitButton from 'components/Form/SubmitButton';
import Icon from 'components/Icon';
import Layout from 'components/Layout';
import {FormFields} from 'types/utils';
import {setUrl} from 'utils/request';
import {onApiFormError} from 'utils/request/result';
import {useSendMagicLink} from './services';

type FormData = FormFields<{
    email: string;
    remember: boolean;
}>;

type MagicLinkFormProps = {
    defaultEmail: string;
    hasRemember: boolean;
    next: string;
    partnerName?: string;
    sessionMinutes?: number;
};

const MagicLinkForm: FC<MagicLinkFormProps> = ({
    defaultEmail,
    hasRemember,
    next,
    partnerName,
    sessionMinutes,
}) => {
    const {t} = useTranslation('Portal');
    const methods = useForm<FormData>({
        defaultValues: {
            email: defaultEmail,
            remember: hasRemember,
        },
    });
    const sendLink = useSendMagicLink();
    const {handleSubmit} = methods;

    const onSendLink = async ({email, remember}: FormData) => {
        const submitData = {
            email,
            next,
            remember,
            session_minutes: sessionMinutes,
        };

        await sendLink
            .mutateAsync(submitData)
            .then(() => {
                setUrl(
                    `${window.Urls.portal_send_link_success_view()}?email=${encodeURIComponent(
                        email,
                    )}`,
                );
            })
            .catch(onApiFormError(methods));
    };

    return (
        <div className="rounded-lg border border-grey-300 bg-white px-6 pb-12 pt-6 shadow-lg">
            <FormProvider {...methods}>
                <h1 className="mt-2">{t('login.title')}</h1>
                {partnerName ? (
                    <p className="my-2 text-base">
                        <Trans
                            components={[<strong key={0} />]}
                            i18nKey="login.partnerSubtitle"
                            ns="Portal"
                            values={{
                                partnerName,
                            }}
                        />
                    </p>
                ) : (
                    <p className="my-2 text-base">{t('login.subtitle')}</p>
                )}
                <form onSubmit={handleSubmit(onSendLink)}>
                    <Layout.Stacked className="hide-required">
                        <div className="mt-4">
                            <Field
                                className="flex flex-col sm:flex-row"
                                name="email"
                                type="input"
                            >
                                <InputEmail
                                    autoFocus={true}
                                    className="grow"
                                    label={t('login.email')}
                                    name="email"
                                    required={true}
                                    requiredType="asterisk"
                                />
                                <SubmitButton
                                    aria-label="send-magic-link"
                                    className="sm:ml-3 mt-6 align-top mb-auto"
                                    kind="primary"
                                >
                                    {t('login.submit')}
                                </SubmitButton>
                            </Field>
                            <Checkbox
                                className="mt-4"
                                label={
                                    <div className="text-sm">
                                        {t('login.rememberMe')}
                                    </div>
                                }
                                name="remember"
                            />
                        </div>
                    </Layout.Stacked>
                </form>
                <FormResult className="mt-4" />
            </FormProvider>
            <div className="mt-8 flex flex-row">
                <a
                    className="flex grow"
                    href="https://help.makeleaps.jp/ja/articles/8687205"
                    rel="noreferrer"
                    target="_blank"
                >
                    <Icon
                        className="my-1 mr-2 text-green-700"
                        icon="open_blank"
                        size="base"
                    />
                    {t('login.portalLink')}
                </a>
                {sessionMinutes && (
                    <span className="text-sm text-grey-500">
                        {t('sessionCustomExpiration', {sessionMinutes})}
                    </span>
                )}
            </div>
        </div>
    );
};

export default MagicLinkForm;
